import {Injectable} from "@angular/core";
import {Observable, Subject} from "rxjs";
import {tap} from "rxjs/operators";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {ApiUrl} from "../../../../../shared/models/apiurl.models";
import {LoadingMaskServiceService} from "../../../../../components/loading-frame/loading-frame.component";
import {FilterBase} from "../../../../../models/filter-base";
import {StringUtils} from "../../../../../utils/StringUtils";


@Injectable()
export class FilterbaseService {
  private subject = new Subject<any>();
  private removeSubject = new Subject<any>();
  private options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.apiKey.getJWTToken()
    })
  }
  filterBases: FilterBase[] = [];
  filterBaseMap = new Map();

  limit = 36;
  offset = 1;
  /** SCRUM-253 "חיפוש באזור זה": minLng,minLat,maxLng,maxLat, or null. */
  mapBbox: string | null = null;
  /** Pages (num_pages) of the last list response - the list container reads
   *  it when the list is reloaded from outside (filters applied). */
  lastTotalPages = 0;

  /** sticky_filter key the map currently belongs to. The service is an
   *  app-wide singleton, so without this the filters of the previous
   *  category stayed in the map after an in-app navigation (footer / home /
   *  search links), were posted with the new category and were written into
   *  ITS sticky key. */
  private scopeKey: string | null = null;

  constructor(private http: HttpClient, private apiKey: ApiUrl, private loadingMaskServiceService: LoadingMaskServiceService) {
    this.ensureScope();
  }

  /** Make the map belong to the current /shop/<main>/category/<sub> page:
   *  on a page change clear it and restore that page's own sticky filters,
   *  then a shared ?filters= link (which wins). Same page = no-op. */
  ensureScope(): void {
    const key = this.getStorageKey();
    if (key === this.scopeKey) return;
    this.scopeKey = key;
    this.filterBaseMap.clear();
    this.offset = 1;
    this.lastTotalPages = 0;
    this.restoreFromStorage();
    this.restoreFromUrl();
  }

  /** ?filters=<JSON> - also when the filter panel is closed (phones open it
   *  on demand, so the shared link used to be ignored until it was opened). */
  private restoreFromUrl(): void {
    try {
      if (typeof window === 'undefined') return;
      const encoded = new URLSearchParams(window.location.search).get('filters');
      if (!encoded) return;
      const obj = JSON.parse(encoded);
      if (!obj || typeof obj !== 'object') return;
      this.filterBaseMap.clear();
      Object.entries(obj).forEach(([key, value]) => this.filterBaseMap.set(key, value));
    } catch {
      /* Ignore malformed shared URLs. */
    }
  }

  // ─── Storage key per namespace ───────────────────────────────────────────────

  private getStorageKey(): string {
    const mainCategory = this.apiKey.getNameSpacePos('/', 3);
    const subCategory = this.apiKey.getNameSpacePos('/', 5);
    return `sticky_filter::${mainCategory}::${subCategory}`;
  }

  // ─── Persist filterBaseMap to localStorage ───────────────────────────────────

  private saveToStorage(): void {
    try {
      const obj: Record<string, any> = {};
      this.filterBaseMap.forEach((value, key) => obj[key] = value);
      localStorage.setItem(this.getStorageKey(), JSON.stringify(obj));
    } catch (e) {
      console.warn('FilterbaseService: failed to save filters to storage', e);
    }
  }

  // ─── Restore filterBaseMap from localStorage ─────────────────────────────────

  restoreFromStorage(): void {
    try {
      const raw = localStorage.getItem(this.getStorageKey());
      if (!raw) return;
      const obj = JSON.parse(raw);
      Object.entries(obj).forEach(([key, value]) => {
        this.filterBaseMap.set(key, value);
      });
    } catch (e) {
      console.warn('FilterbaseService: failed to restore filters from storage', e);
    }
  }

  // ─── Clear sticky filters for current namespace ──────────────────────────────

  clearStorage(): void {
    localStorage.removeItem(this.getStorageKey());
  }

  // ─── Existing methods with save hooks ────────────────────────────────────────

  clearRelatedFields(questionKey: string, source: string | null = null): void {
    const relatedField = this.filterBases.find(qs => qs.key === questionKey)?.relatedField;
    if (StringUtils.isEmpty(relatedField)) return;
    const arrList = relatedField!.split(',').map(field => field.trim()).filter(field => !StringUtils.isEmpty(field));
    if (arrList == null || arrList!.length == 0) return;
    arrList.forEach((field: any) => {
      this.filterBaseMap.delete(field);
      this.removeSubject.next(field);
    })
    if (source == "clearSearch") {
      this.deleteQuestion(questionKey);
    }
  }

  addQuestion(index: string, answer: any) {
    this.clearRelatedFields(index);
    this.filterBaseMap.set(index, answer);
    this.saveToStorage();  // ← persist
    this.sendClickEvent(index);
  }

  deleteQuestion(index: string, direction: string = null) {
    this.clearRelatedFields(index);
    this.filterBaseMap.delete(index);
    this.saveToStorage();  // ← persist
    this.sendClickEvent(index);
  }

  getQuestion(index: string) {
    return this.filterBaseMap.get(index);
  }

  getActiveQuestions(): Array<{ key: string; value: any }> {
    return Array.from(this.filterBaseMap.entries()).map(([key, value]) => ({key, value}));
  }

  replaceQuestions(questions: Record<string, any>): void {
    this.filterBaseMap.clear();
    Object.entries(questions || {}).forEach(([key, value]) => this.filterBaseMap.set(key, value));
    this.saveToStorage();
    this.sendClickEvent('__restore__');
  }

  clearAllQuestions(): void {
    const keys = Array.from(this.filterBaseMap.keys());
    this.filterBaseMap.clear();
    this.saveToStorage();
    keys.forEach(key => this.removeSubject.next(key));
    this.sendClickEvent('__clear_all__');
  }

  loadFilterData(limit: number, offset: number): Observable<any> {
    this.ensureScope();
    var arrJson: any = {}
    const mainCategory = this.apiKey.getNameSpacePos('/', 3);
    const subCategory = this.apiKey.getNameSpacePos('/', 5);
    arrJson['mainCategory'] = mainCategory;
    arrJson['subCategory'] = subCategory;
    this.filterBaseMap.forEach((value, key) => arrJson[key] = value);
    return this.http.post(this.apiKey.getFilterBasePostUrl(mainCategory, limit, offset, this.mapBbox), this.apiKey.buildPayload(mainCategory, subCategory, arrJson), this.options)
      .pipe(tap((data: any) => {
        if (data && data.page_size != null) this.lastTotalPages = data.page_size;
      }));
  }

  /** SCRUM-253: map points for the same filters as the list (whole country –
   *  the map shows everything, the list may be limited to the map area). */
  loadMapPoints(): Observable<any> {
    this.ensureScope();
    const arrJson: any = {};
    const mainCategory = this.apiKey.getNameSpacePos('/', 3);
    const subCategory = this.apiKey.getNameSpacePos('/', 5);
    arrJson['mainCategory'] = mainCategory;
    arrJson['subCategory'] = subCategory;
    this.filterBaseMap.forEach((value, key) => arrJson[key] = value);
    return this.http.post(this.apiKey.getNadlanMapPointsUrl(mainCategory), this.apiKey.buildPayload(mainCategory, subCategory, arrJson), this.options);
  }

  sendClickEvent(fieldChange: string) {
    this.subject.next(fieldChange);
  }

  getClickEvent(): Observable<any> {
    return this.subject.asObservable();
  }

  sendRemoveEvent(fieldChange: any) {
    this.removeSubject.next(fieldChange);
  }

  getRemoveEvent(): Observable<any> {
    return this.removeSubject.asObservable();
  }
}
